import type { CookieConsentValue } from '@forgd/shared/utils/cookie-consent'
import { CONSENT_COOKIE_NAME, CONSENT_COOKIE_VERSION } from '@forgd/shared/constants'
import { consentStateFromValue, resolveApexCookieDomain } from '@forgd/shared/utils/cookie-consent'

export function useCookieConsent() {
  const enabled = Boolean(useRuntimeConfig().public.cookieConsentEnabled)

  const cookie = useCookie<CookieConsentValue | null>(CONSENT_COOKIE_NAME, {
    default: () => null,
    domain: resolveApexCookieDomain(useRequestURL().hostname), // .forgd.com (app & liquidity)
    maxAge: 60 * 60 * 24 * 365,
    path: '/',
    sameSite: 'lax',
    secure: true,
  })

  const state = useState<CookieConsentValue | null>('cookie-consent', () => cookie.value)

  const reopened = useState('cookie-consent-reopened', () => false)

  const analyticsConsent = computed(() => {
    const stored = consentStateFromValue(state.value)
    if (stored === 'denied') {
      return stored
    }
    if (!enabled) {
      return 'granted'
    }
    return stored
  })

  const bannerVisible = computed(() => enabled && (reopened.value || analyticsConsent.value === 'unset'))

  function persist(analytics: boolean) {
    const value: CookieConsentValue = {
      analytics,
      timestamp: new Date().toISOString(),
      version: CONSENT_COOKIE_VERSION,
    }
    cookie.value = value
    state.value = value
    reopened.value = false
  }

  return {
    enabled,
    bannerVisible,
    analyticsConsent,
    accept: () => persist(true),
    reject: () => persist(false),
    openPreferences: () => {
      reopened.value = true
    },
    reset: () => {
      cookie.value = null
      state.value = null
      reopened.value = false
    },
  }
}
